import { useState, useEffect, useMemo } from 'react'
import { TProcedureFilters } from 'lib/hooks/useFilters'

export interface UseHospitalExpansionProps {
  filters: TProcedureFilters
  hospitalNames: string[]
}

export interface UseHospitalExpansionReturn {
  expandedHospitals: Set<string>
  shouldAutoExpand: boolean
  toggleHospital: (hospitalName: string) => void
  expandAll: () => void
  collapseAll: () => void
}

/**
 * Custom hook to manage hospital expansion state in grouped procedure list
 * Automatically expands hospitals when specific filters are active
 */
export const useHospitalExpansion = ({
  filters,
  hospitalNames,
}: UseHospitalExpansionProps): UseHospitalExpansionReturn => {
  const [expandedHospitals, setExpandedHospitals] = useState<Set<string>>(
    new Set()
  )

  // Auto-expand hospitals if there are active hospital filters (1-2 hospitals)
  const shouldAutoExpand = useMemo(() => {
    return filters.rooms.length > 0 && filters.rooms.length <= 2
  }, [filters.rooms])

  // Create a stable string representation of hospital names for dependency tracking
  const hospitalNamesKey = useMemo(() => {
    return hospitalNames.slice().sort().join(',')
  }, [hospitalNames])

  // Create a stable string representation of active room filters
  const roomFiltersKey = useMemo(() => {
    return filters.rooms.slice().sort().join(',')
  }, [filters.rooms])

  // Handle auto-expansion logic
  useEffect(() => {
    if (shouldAutoExpand && hospitalNamesKey) {
      // Reconstruct hospital names from the stable key
      const currentHospitalNames = hospitalNamesKey
        .split(',')
        .filter((name) => name.length > 0)
      setExpandedHospitals(new Set(currentHospitalNames))
    } else if (filters.rooms.length === 0) {
      setExpandedHospitals(new Set())
    }
  }, [shouldAutoExpand, hospitalNamesKey, roomFiltersKey])

  const toggleHospital = (hospitalName: string) => {
    setExpandedHospitals((prev) => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(hospitalName)) {
        newExpanded.delete(hospitalName)
      } else {
        newExpanded.add(hospitalName)
      }
      return newExpanded
    })
  }

  const expandAll = () => {
    setExpandedHospitals(new Set(hospitalNames))
  }

  const collapseAll = () => {
    setExpandedHospitals(new Set())
  }

  return {
    expandedHospitals,
    shouldAutoExpand,
    toggleHospital,
    expandAll,
    collapseAll,
  }
}
